import React, { useEffect, useRef, useState } from 'react';
import { Input } from './common';
import { useElements } from '@stripe/react-stripe-js';

function CustomCardElement({ stripe, onChange, onElementsReady }) {
  const elements = useElements()
  const [cardholderName, setCardholderName] = useState('');
  const numberRef = useRef(null);
  const expiryRef = useRef(null);
  const cvcRef = useRef(null);
  const cardNumberElement = useRef(null);
  const cardExpiryElement = useRef(null);
  const cardCvcElement = useRef(null);

  const handleNameChange = (e) => {
    setCardholderName(e.target.value);
    if (onChange) {
      onChange({ type: 'name', value: e.target.value });
    }
  };

  useEffect(() => {
    if (!stripe || !elements) return;
    // Avoid double mounting
    if (cardNumberElement.current || cardExpiryElement.current || cardCvcElement.current) return;
    if (!numberRef.current || !expiryRef.current || !cvcRef.current) return;

    const style = {
      base: {
        fontSize: '16px',
        color: '#32325d',
        '::placeholder': { color: '#a0aec0' },
      },
      invalid: { color: '#e53e3e' },
    };

    const number = elements.create('cardNumber', { style });
    const expiry = elements.create('cardExpiry', { style });
    const cvc = elements.create('cardCvc', { style });

    number.mount(numberRef.current);
    expiry.mount(expiryRef.current);
    cvc.mount(cvcRef.current);

    cardNumberElement.current = number;
    cardExpiryElement.current = expiry;
    cardCvcElement.current = cvc;

    if (onChange) {
      number.on('change', onChange);
      expiry.on('change', onChange);
      cvc.on('change', onChange);
    }

    // Only call onElementsReady once, after mount
    if (onElementsReady) {
      onElementsReady({
        cardNumber: number,
        cardExpiry: expiry,
        cardCvc: cvc,
      });
    }

    return () => {
      number.destroy();
      expiry.destroy();
      cvc.destroy();
      cardNumberElement.current = null;
      cardExpiryElement.current = null;
      cardCvcElement.current = null;
      // Only call onElementsReady(null) on cleanup
      if (onElementsReady) {
        onElementsReady({ cardNumber: null, cardExpiry: null, cardCvc: null });
      }
    };
  }, [stripe, elements]); // Remove onChange, onElementsReady from deps

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={{ fontSize: 14, marginBottom: 4, display: 'block' }}>Cardholder Name</label>
        <Input
          value={cardholderName}
          onChange={handleNameChange}
          placeholder="Name on card"
          autoComplete="cc-name"
        />
      </div>
      <div>
        <label style={{ fontSize: 14, marginBottom: 4, display: 'block' }}>Card Number</label>
        <Input ref={numberRef} as="div" />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 14, marginBottom: 4, display: 'block' }}>Expiry</label>
          <Input ref={expiryRef} as="div" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 14, marginBottom: 4, display: 'block' }}>CVC</label>
          <Input ref={cvcRef} as="div" />
        </div>
      </div>
    </div>
  );
}

export default CustomCardElement;
